import { Typography, Box, Button } from "@mui/material";
import styles from "../../../index.module.scss";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import TableComponent from "../../../../components/TableComponent";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppState, appActions } from "../../../../store/app";
import AddIcon from "@mui/icons-material/Add";
import Storage from "../../../../utils/storage";
import LoadingContainer from "../../../../components/LoadingContainer";
import { useTranslation } from "../../../../i18n";
import PopupMenu from "../../../../components/PopupMenu";
import ConfirmModal from "../../../../components/ConfirmModal";
import { deleteAction } from "../../../../store/app/appActions";
import { deleteStaffService } from "../../../../store/app/staff/staffService";
import SearchComponent from "../../../../components/SearchComponent";
import ChipComponent, { ChipData } from "../../../../components/ChipComponent";
import { LoadingButton } from "@mui/lab";
import { exportDiseasesAction, getDiseasesAction, searchDiseasesAction } from "../../../../store/expenses/diseases/diseasesActions";
import { expensesActions, ExpensesState } from "../../../../store/expenses";
import { Disease, diseaseColumns, DiseaseRow } from "../../../../interfaces/Expenses/disease";
import DiseaseView from "./DiseaseView";
import FilterDisease from "./FilterDisease";
import AddDisease from "./AddDisease";

const Diseases = () => {
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(50);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [query, setQuery] = useState("");
  const [isInit, setIsInit] = useState(true);
  const dispatch = useDispatch();
  const appState: AppState = useSelector((state: any) => state.app);
  const expensesState: ExpensesState = useSelector((state: any) => state.expenses);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [chipData, setChipData] = useState([]);
  const [disease, setDisease] = useState<any>();
  const [viewDisease, setViewDisease] = useState<boolean>();
  const open = Boolean(anchorEl);
  const token = Storage.getToken();
  const { t } = useTranslation();

  console.log("Disease reload");

  useEffect(() => {
    dispatch(
      getDiseasesAction(
        `?page=${page}&sort=-createdAt&limit=${rowsPerPage}`,
        token
      )
    );
  }, []);

  useEffect(() => {
    if (!expensesState.fetchedDiseases) {
      dispatch(
        getDiseasesAction(
          `?page=${page}&sort=-createdAt&limit=${rowsPerPage}${query}`,
          token
        )
      );
    }
  }, [dispatch, expensesState.fetchedDiseases, page, query, rowsPerPage, token]);

  useEffect(() => {
    if (!appState.isFetching) {
      setCount(appState.count);
    }
  }, [appState.isFetching, appState.count]);

  useEffect(() => {
    if (chipData.length > 0) {
      let queryString = "";
      chipData.forEach((data: any) => {
        queryString += `&${data.id}=${data.filter}`;
      });
      setQuery(queryString);
      dispatch(expensesActions.setFetchedDiseases(false));
    } else if (chipData.length === 0 && query !== "") {
      setQuery("");
      dispatch(expensesActions.setFetchedDiseases(false));
    }
  }, [chipData, dispatch, query]);

  useEffect(() => {
    let timer = setTimeout(() => {
      if (searchText) {
        dispatch(searchDiseasesAction(`?text=${searchText}`, token));
        setIsInit(false);
      } else {
        if (!appState.isFetching && !isInit) {
          dispatch(expensesActions.setFetchedDiseases(false));
        }
      }
    }, 1000);

    return () => {
      clearTimeout(timer);
    };
  }, [appState.isFetching, dispatch, isInit, searchText, token]);

  const createData = (disease: Disease): DiseaseRow => {
    return {
      name: disease.name,
      type: disease.category,
      severity: disease.severity,
      action: (
        <button
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
            setDisease(disease);
            setAnchorEl(event.currentTarget);
          }}
          className={styles.actionsIcon}
        >
          •••
        </button>
      ),
    };
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
    dispatch(expensesActions.setFetchedDiseases(false));
  };

  const filterHandler = (filterData: any) => {
    setChipData(filterData);
  };

  const handleChipDelete = (chipToDelete: ChipData) => {
    setChipData((chips) =>
      chips.filter((chip: ChipData) => chip.key !== chipToDelete.key)
    );
  };

  const exportExcelHandler = () => {
    dispatch(exportDiseasesAction("?limit=1000", token, t("diseases")));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    dispatch(expensesActions.setFetchedDiseases(false));
  };

  const deleteStaffHandler = () => {
    dispatch(
      deleteAction(
        deleteStaffService,
        disease?._id,
        token,
        t("staff"),
        () => {},
        () => dispatch(expensesActions.setFetchedDiseases(false))
      )
    );
  };

  const rows = expensesState.diseases?.map((disease: Disease) => createData(disease));

  return (
    <>
      <Box className={styles.topContainer}>
        <Box>{t("welcome")}</Box>
        <Box className={styles.dFlex}>
          <SearchComponent setSearchText={setSearchText} />
          <LoadingButton
            variant="contained"
            startIcon={<SaveAltIcon />}
            loading={appState.isExportingExcel}
            onClick={exportExcelHandler}
            className={styles.exportExcelButton}
          >
            {t("exportExcel")}
          </LoadingButton>
        </Box>
      </Box>
      {appState.isFetching && <LoadingContainer />}
      {!appState.isFetching && (
        <>
          {chipData.length > 0 && (
            <ChipComponent
              chipData={chipData}
              handleDelete={handleChipDelete}
            />
          )}
          <Box className={styles.tableTopActions}>
            <Box>
              <Typography>{t("diseases")}</Typography>
            </Box>
            <Box>
              <Button
                onClick={() => dispatch(appActions.setOpenFilter(true))}
                startIcon={<AddIcon />}
              >
                {t("filter")}
              </Button>
              <Button
                onClick={() =>
                  dispatch(
                    appActions.setOpenForm({ open: true, action: "add" })
                  )
                }
                startIcon={<AddIcon />}
              >
                {t("addNew")}
              </Button>
            </Box>
          </Box>
          <TableComponent
            rows={rows}
            columns={diseaseColumns}
            count={count}
            page={page}
            rowsPerPage={rowsPerPage}
            handlePageChange={handlePageChange}
            handleRowsPerPageChange={handleRowsPerPageChange}
          />
          {open && (
            <PopupMenu
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              onView={() => setViewDisease(true)}
              onUpdate={() =>
                dispatch(
                  appActions.setOpenForm({ open: true, action: "update" })
                )
              }
              onDelete={() => dispatch(appActions.setIsDeleting(true))}
            />
          )}
          {appState.isDeleting && (
            <ConfirmModal
              onConfirm={() => deleteStaffHandler()}
              content={`${t("confirmDeleteDesc")} ${disease?.name} ?`}
            />
          )}
          {appState.openForm.open && <AddDisease disease={disease} />}
          {appState.openFilter && (
            <FilterDisease
              open={appState.openFilter}
              close={() => dispatch(appActions.setOpenFilter(false))}
              filterHandler={filterHandler}
            />
          )}
          {viewDisease && (
            <DiseaseView
              open={viewDisease}
              onClose={() => setViewDisease(false)}
              disease={disease}
            />
          )}
        </>
      )}
    </>
  );
};

export default Diseases;
